import React, { useState, useEffect, useRef } from 'react';

interface InlineEditProps {
    value: string;
    onChange: (value: string) => void;
    onBlur: (value: string) => void;
    className?: string;
    placeholder?: string;
    readOnly?: boolean;
    multiline?: boolean;
    as?: 'input' | 'div'; // For contentEditable vs input
    ariaLabel?: string;
}

const NativeInput = 'input';

export const InlineEdit: React.FC<InlineEditProps> = ({ 
    value, 
    onChange, 
    onBlur,
    className = '',
    placeholder = '',
    readOnly = false,
    multiline = false,
    as = 'input',
    ariaLabel
}) => {
    const [localValue, setLocalValue] = useState(value);
    const ref = useRef<HTMLInputElement | HTMLDivElement>(null);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleChange = (e: React.FormEvent<HTMLInputElement | HTMLDivElement>) => {
        const newValue = as === 'input' 
            ? (e.target as HTMLInputElement).value 
            : e.currentTarget.textContent || '';
        setLocalValue(newValue);
        onChange(newValue);
    };

    const handleBlur = () => {
        onBlur(localValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !multiline) {
            e.preventDefault();
            ref.current?.blur();
        }
    };

    if (as === 'div') {
        return (
            // biome-ignore lint/a11y/useSemanticElements: contentEditable is the inline editor surface; replacing it with a textarea changes layout and editing behavior.
            <div
                ref={ref as React.RefObject<HTMLDivElement>}
                dir="ltr"
                contentEditable={!readOnly}
                suppressContentEditableWarning
                onInput={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className={className}
                data-placeholder={placeholder}
                role="textbox"
                aria-label={ariaLabel ?? placeholder}
                aria-multiline={multiline}
                tabIndex={readOnly ? undefined : 0}
            >
                {value}
            </div>
        );
    }

    return (
        <NativeInput
            ref={ref as React.RefObject<HTMLInputElement>}
            type="text"
            dir="ltr"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={className}
            placeholder={placeholder}
            readOnly={readOnly}
            aria-label={ariaLabel}
        />
    );
};














